/**
 * ── SPREADSHEET → LISTING TABLE ROWS (2026-08-21, Jack: "should also have an
 * option to upload a spreadsheet for listing table manual if desired") ─────────
 *
 * Divisions keep their homesite lists in Excel; retyping ten rows into a panel
 * is the kind of work this tool exists to remove. The upload takes a CSV export
 * (Excel/Sheets: File → Save As → CSV — no xlsx parser ships in this bundle,
 * and a 300KB dependency for one import path is not an MVP trade), maps the
 * columns by header NAME, and hands back groups ready for the section packer.
 *
 * Column matching is deliberately loose — "Sq Ft", "sqft", "SQFT", "Square
 * Feet" all land on SqFt — because the source is whatever a division already
 * has, not a template we handed them. An optional Collection/Community column
 * splits rows into their sections; without it every row lands in the first
 * section and overflows into the next. Values are passed through UNTOUCHED:
 * the block's own guardrails (caps, formats) apply on write and at render,
 * same as typed input — this file maps, it does not invent or "fix".
 */

export interface SheetHome { [k: string]: string }
export interface SheetGroup { name: string; homes: SheetHome[] }

/** header → declared home-field suffix; first match wins, unmatched columns are ignored */
const COLS: [RegExp, string][] = [
  [/^\s*(homesite|home\s*site|lot|site)\s*(#|no\.?|number)?\s*$/i, "Homesite"],
  [/^\s*(plan|floor\s*plan|plan\s*name|model)\s*$/i, "Plan"],
  [/^\s*(sq\.?\s*ft\.?|sqft|square\s*(feet|footage)|size)\s*$/i, "SqFt"],
  [/^\s*(beds?|bedrooms?|bd)\s*$/i, "Beds"],
  [/^\s*(baths?|bathrooms?|ba)\s*$/i, "Baths"],
  [/^\s*(price|list\s*price|base\s*price)\s*$/i, "Price"],
  [/^\s*(url|link|view\s*(home)?\s*(url|link)?|website)\s*$/i, "Url"],
];
const GROUP_COL = /^\s*(collection|community|section|group|neighborhood)\s*(name)?\s*$/i;

/** one CSV line → cells, honouring quoted commas and doubled quotes */
export function csvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "", q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/**
 * The whole file → section groups. Returns an error string instead of throwing:
 * the caller shows it in the panel's own flash, never a blank failure.
 */
export function sheetHomes(text: string): { groups: SheetGroup[]; err?: string } {
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2) return { groups: [], err: "That file has no data rows — the first line must be headers." };
  const heads = csvLine(lines[0]);
  const map: (string | null)[] = heads.map((h) => {
    const hit = COLS.find(([re]) => re.test(h));
    return hit ? hit[1] : null;
  });
  const gi = heads.findIndex((h) => GROUP_COL.test(h));
  if (!map.some((m) => m === "Homesite" || m === "Plan")) {
    return { groups: [], err: "No Homesite or Plan column found — headers like Homesite, Plan, Sq Ft, Beds, Baths, Price, URL (and optionally Collection)." };
  }
  const groups: SheetGroup[] = [];
  const byName: Record<string, SheetGroup> = {};
  for (let r = 1; r < lines.length; r++) {
    const cells = csvLine(lines[r]);
    const home: SheetHome = {};
    map.forEach((sfx, c) => { if (sfx && cells[c] != null && cells[c] !== "") home[sfx] = cells[c]; });
    if (!Object.keys(home).length) continue;
    const gname = gi > -1 ? (cells[gi] || "") : "";
    let g = byName[gname];
    if (!g) { g = { name: gname, homes: [] }; byName[gname] = g; groups.push(g); }
    g.homes.push(home);
  }
  if (!groups.length) return { groups: [], err: "No rows with a homesite or plan in them." };
  return { groups };
}

/**
 * A FILE of either kind → groups (2026-08-21, Jack: "csv should also take excel
 * as well"). An .xlsx is parsed with ExcelJS — dynamically imported so the parser
 * is a lazy chunk that loads only when someone actually picks an Excel file,
 * never in the app bundle — first worksheet only, converted to CSV and run
 * through the SAME mapper, so both paths obey identical rules: recognised
 * headers only, unknown columns IGNORED, values passed through untouched for the
 * block's own guardrails to govern.
 *
 * WHY EXCELJS, NOT SHEETJS (2026-08-22): npm's `xlsx` is abandoned at 0.18.5
 * with two HIGH CVEs (prototype pollution + ReDoS) and no patched npm release —
 * it failed PR #26's required Dependency Review gate outright, and the patched
 * SheetJS lives only on their own CDN, which corporate networks block. ExcelJS
 * is current on npm with no high advisories (its `unzipper` is overridden to
 * 0.12.x in package.json to keep the deprecated `fstream` chain out — that
 * carried its own unpatched HIGH). One trade: ExcelJS reads .xlsx only, so a
 * legacy .xls now gets the re-save message instead of a parse.
 */
export async function sheetHomesFile(f: File): Promise<{ groups: SheetGroup[]; err?: string }> {
  const isExcel = /\.(xlsx|xls)$/i.test(f.name || "") || /spreadsheetml|ms-excel/.test(f.type || "");
  try {
    if (isExcel) {
      const ExcelJS = await import("exceljs");
      const buf = await new Promise<ArrayBuffer>((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result as ArrayBuffer);
        rd.onerror = () => rej(rd.error);
        rd.readAsArrayBuffer(f);
      });
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buf);
      const ws = wb.worksheets[0];
      if (!ws) return { groups: [], err: "That workbook has no sheets in it." };
      /* worksheet → CSV, quoting only what CSV needs quoted; `cell.text` renders
         formula results and rich text the way the sheet shows them */
      const q = (x: string) => (/[",\n]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x);
      const lines: string[] = [];
      ws.eachRow({ includeEmpty: true }, (row) => {
        const vals: string[] = [];
        const cc = ws.columnCount || (row.cellCount as number) || 0;
        for (let ci = 1; ci <= cc; ci++) vals.push(q(String(row.getCell(ci).text ?? "")));
        lines.push(vals.join(","));
      });
      return sheetHomes(lines.join("\n"));
    }
    const text = await new Promise<string>((res, rej) => {
      const rd = new FileReader();
      rd.onload = () => res(String(rd.result || ""));
      rd.onerror = () => rej(rd.error);
      rd.readAsText(f);
    });
    return sheetHomes(text);
  } catch {
    return { groups: [], err: "Couldn't read that file — try re-saving it as .xlsx or .csv." };
  }
}
